"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

// ── Data ───────────────────────────────────────────────────────────────

const YEAR_DATA = [
  { year: 1, price: 10000, dividend: 300 },
  { year: 2, price: 14000, dividend: 330 },
  { year: 3, price: 11500, dividend: 365 },
  { year: 4, price: 18000, dividend: 400 },
  { year: 5, price: 24000, dividend: 440 },
  { year: 6, price: 19000, dividend: 485 },
  { year: 7, price: 28000, dividend: 535 },
  { year: 8, price: 35000, dividend: 590 },
  { year: 9, price: 40000, dividend: 650 },
  { year: 10, price: 50000, dividend: 715 },
] as const;

const EARLY = { idx: 0, color: COLOR.emerald } as const;
const LATE = { idx: YEAR_DATA.length - 1, color: COLOR.rose } as const;

const STRINGS: Record<
  Locale,
  {
    early: { label: string; buyYear: string };
    late: { label: string; buyYear: string };
    priceAxis: string;
    dividendAxis: string;
    now: string;
    yearsAgo: (years: number) => string;
    boughtAt: (label: string, price: string) => string;
    cardHeader: (label: string, buyYear: string) => string;
    invested: string;
    annualDividend: string;
    yieldOnCost: string;
    money: (value: string) => string;
    formula: (dividend: string, price: string) => string;
    title: string;
    description: (dividend: string) => string;
    bothHold: (dividend: string) => string;
  }
> = {
  ja: {
    early: { label: "Aさん", buyYear: "10年前に" },
    late: { label: "Bさん", buyYear: "今" },
    priceAxis: "株価（円）",
    dividendAxis: "1株配当（円）",
    now: "今",
    yearsAgo: (years) => `${years}年前`,
    boughtAt: (label, price) => `${label}が${price}円で1株購入`,
    cardHeader: (label, buyYear) => `${label}（${buyYear}購入）`,
    invested: "投資額（買値）",
    annualDividend: "年間配当",
    yieldOnCost: "取得利回り",
    money: (value) => `${value}円`,
    formula: (dividend, price) => `${dividend}円 ÷ ${price}円`,
    title: "いつ買ったかで、配当の意味が変わる",
    description: (dividend) => `同じ配当 ${dividend}円でも、投資額が違えばリターンはまったく違う`,
    bothHold: (dividend) => `どちらも1株保有・今の年間配当 ${dividend}円 だが...`,
  },
  en: {
    early: { label: "Investor A", buyYear: "10 years ago" },
    late: { label: "Investor B", buyYear: "now" },
    priceAxis: "Share price (¥)",
    dividendAxis: "Dividend per share (¥)",
    now: "Now",
    yearsAgo: (years) => `${years}y ago`,
    boughtAt: (label, price) => `${label} bought 1 share at ¥${price}`,
    cardHeader: (label, buyYear) => `${label} (bought ${buyYear})`,
    invested: "Amount invested (buy price)",
    annualDividend: "Annual dividend",
    yieldOnCost: "Yield on cost",
    money: (value) => `¥${value}`,
    formula: (dividend, price) => `¥${dividend} ÷ ¥${price}`,
    title: "When you bought changes what the dividend means",
    description: (dividend) =>
      `The same ¥${dividend} dividend gives a completely different return depending on what you paid`,
    bothHold: (dividend) => `Both hold 1 share and receive the same ¥${dividend} annual dividend, but...`,
  },
};

// ── Helpers ─────────────────────────────────────────────────────────────

function fmt(n: number) {
  return n.toLocaleString();
}

function yieldPct(dividend: number, price: number) {
  return Math.round((dividend / price) * 1000) / 10;
}

function PersonIcon({ x, y, size = 16, color }: { x: number; y: number; size?: number; color: string }) {
  const s = size;
  return (
    <g opacity={0.65}>
      <circle cx={x + s / 2} cy={y + s * 0.25} r={s * 0.22} fill={color} />
      <ellipse cx={x + s / 2} cy={y + s * 0.75} rx={s * 0.35} ry={s * 0.25} fill={color} />
    </g>
  );
}

// ── Layout ──────────────────────────────────────────────────────────────

const W = 460;
const PAD = 16;
const CONTENT_W = W - PAD * 2;

// Chart
const CHART_L = PAD + 48;
const CHART_R = W - PAD - 36;
const CHART_W = CHART_R - CHART_L;
const CALLOUT_Y = 8;
const CHART_TOP = CALLOUT_Y + 28 + 14;
const CHART_H = 140;
const CHART_BOT = CHART_TOP + CHART_H;

const PRICE_MAX = 60000;
const DIV_MAX = 800;
const STEP_X = CHART_W / YEAR_DATA.length;
const BAR_W = STEP_X - 6;

function priceToY(p: number) {
  return CHART_BOT - (p / PRICE_MAX) * CHART_H;
}

const LINE_PTS = YEAR_DATA.map((d, i) => ({
  x: CHART_L + i * STEP_X + STEP_X / 2,
  y: priceToY(d.price),
}));
const LINE_PATH = LINE_PTS.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");

// ── Chart sub-components ───────────────────────────────────────────────

function PriceAxis() {
  const t = STRINGS[useDocLocale()];
  const ticks = [0, 10000, 20000, 30000, 40000, 50000];
  return (
    <g>
      <text x={PAD} y={CHART_TOP - 6} fontSize="8" fill="currentColor" fontWeight="600" opacity={0.75}>
        {t.priceAxis}
      </text>
      {ticks.map((v) => (
        <g key={v}>
          <line
            x1={CHART_L}
            y1={priceToY(v)}
            x2={CHART_R}
            y2={priceToY(v)}
            stroke="currentColor"
            strokeWidth="0.5"
            opacity={0.18}
          />
          <text
            x={CHART_L - 4}
            y={priceToY(v)}
            textAnchor="end"
            fontSize="7"
            fill="currentColor"
            opacity={0.45}
            dominantBaseline="central"
          >
            {fmt(v)}
          </text>
        </g>
      ))}
    </g>
  );
}

function DividendAxis() {
  const t = STRINGS[useDocLocale()];
  const ticks = [0, 200, 400, 600, 800];
  return (
    <g>
      <text
        x={W - PAD}
        y={CHART_TOP - 6}
        textAnchor="end"
        fontSize="8"
        fill={COLOR.sky}
        fontWeight="600"
        opacity={0.75}
      >
        {t.dividendAxis}
      </text>
      {ticks.map((v) => (
        <text
          key={v}
          x={CHART_R + 4}
          y={CHART_BOT - (v / DIV_MAX) * CHART_H}
          fontSize="7"
          fill={COLOR.sky}
          opacity={0.55}
          dominantBaseline="central"
        >
          {v}
        </text>
      ))}
    </g>
  );
}

function DividendBars() {
  const t = STRINGS[useDocLocale()];
  return (
    <g>
      {/* 行ヘッダー不要 — 1年目ラベルで文脈が伝わる */}
      {YEAR_DATA.map((d, i) => {
        const bx = CHART_L + i * STEP_X + (STEP_X - BAR_W) / 2;
        const bh = (d.dividend / DIV_MAX) * CHART_H;
        const isEndpoint = i === 0 || i === YEAR_DATA.length - 1;
        return (
          <g key={d.year}>
            <rect
              x={bx}
              y={CHART_BOT - bh}
              width={BAR_W}
              height={bh}
              rx={3}
              fill={COLOR.sky}
              opacity={isEndpoint ? 0.45 : 0.22}
            />
            <text
              x={bx + BAR_W / 2}
              y={CHART_BOT + 11}
              textAnchor="middle"
              fontSize="6.5"
              fill={i === YEAR_DATA.length - 1 ? LATE.color : "currentColor"}
              opacity={0.65}
            >
              {i === YEAR_DATA.length - 1 ? t.now : t.yearsAgo(YEAR_DATA.length - i)}
            </text>
          </g>
        );
      })}
    </g>
  );
}

function PriceLine() {
  return (
    <g>
      <path d={LINE_PATH} fill="none" stroke="currentColor" strokeWidth="2" opacity={0.55} />
      {LINE_PTS.map((p, i) => {
        const isFirst = i === 0;
        const isLast = i === LINE_PTS.length - 1;
        const isEndpoint = isFirst || isLast;
        return (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={isEndpoint ? 4 : 2.5}
            fill="white"
            stroke={isFirst ? EARLY.color : isLast ? LATE.color : "currentColor"}
            strokeWidth={isEndpoint ? "2" : "1.2"}
            strokeOpacity={isEndpoint ? 1 : 0.45}
          />
        );
      })}
    </g>
  );
}

function BuyCallout({
  ptIndex,
  buyPrice,
  color,
  textAnchor = "middle" as const,
}: {
  ptIndex: number;
  buyPrice: number;
  color: string;
  textAnchor?: "middle" | "end";
}) {
  const t = STRINGS[useDocLocale()];
  const pt = LINE_PTS[ptIndex];
  const label = ptIndex === 0 ? "A" : "B";
  return (
    <g>
      <text
        x={pt.x}
        y={CALLOUT_Y + 10}
        textAnchor={textAnchor}
        fontSize="8"
        fill={color}
        fontWeight="700"
        opacity={0.9}
      >
        {t.boughtAt(label, fmt(buyPrice))}
      </text>
      <line x1={pt.x} y1={CALLOUT_Y + 14} x2={pt.x} y2={pt.y - 6} stroke={color} strokeWidth="1.2" opacity={0.45} />
      <circle cx={pt.x} cy={pt.y - 6} r={1.5} fill={color} opacity={0.65} />
    </g>
  );
}

// ── Investor card (simplified) ─────────────────────────────────────────

function InvestorCard({
  x,
  y,
  width,
  height,
  label,
  buyYear,
  buyPrice,
  currentDiv,
  color,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  label: string;
  buyYear: string;
  buyPrice: number;
  currentDiv: number;
  color: string;
}) {
  const t = STRINGS[useDocLocale()];
  const lx = x + 14;
  const rx = x + width - 14;
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx={8}
        fill={color}
        opacity={0.1}
        stroke={color}
        strokeWidth="1.5"
        strokeOpacity={0.4}
      />

      {/* ヘッダー */}
      <PersonIcon x={x + 10} y={y + 7} size={14} color={color} />
      <text x={x + 28} y={y + 17} fontSize="11" fill={color} fontWeight="700">
        {t.cardHeader(label, buyYear)}
      </text>

      {/* 買値 */}
      <text x={lx} y={y + 35} fontSize="9" fill="currentColor" opacity={0.65}>
        {t.invested}
      </text>
      <text x={rx} y={y + 35} textAnchor="end" fontSize="10" fill={color} fontWeight="700">
        {t.money(fmt(buyPrice))}
      </text>

      {/* 配当 */}
      <text x={lx} y={y + 50} fontSize="9" fill="currentColor" opacity={0.65}>
        {t.annualDividend}
      </text>
      <text x={rx} y={y + 50} textAnchor="end" fontSize="10" fill="currentColor" fontWeight="600" opacity={0.75}>
        {t.money(fmt(currentDiv))}
      </text>

      {/* 区切り */}
      <line x1={lx} y1={y + 57} x2={rx} y2={y + 57} stroke="currentColor" strokeWidth="0.5" opacity={0.18} />

      {/* 取得利回り（メイン） */}
      <rect x={x + 8} y={y + 62} width={width - 16} height={40} rx={6} fill={color} opacity={0.14} />
      <text x={x + 18} y={y + 76} fontSize="9" fill={color} fontWeight="600">
        {t.yieldOnCost}
      </text>
      <text x={x + 18} y={y + 93} fontSize="7.5" fill={color} opacity={0.6}>
        {t.formula(fmt(currentDiv), fmt(buyPrice))}
      </text>
      <text x={rx - 8} y={y + 91} textAnchor="end" fontSize="20" fill={color} fontWeight="800">
        {yieldPct(currentDiv, buyPrice)}%
      </text>
    </g>
  );
}

// ── Main ────────────────────────────────────────────────────────────────

export function DocDividendMetricsDiagram() {
  const t = STRINGS[useDocLocale()];
  const currentDiv = YEAR_DATA[LATE.idx].dividend;

  const compY = CHART_BOT + 30;
  const colGap = 14;
  const colW = (CONTENT_W - colGap) / 2;
  const colH = 110;
  const totalH = compY + colH + 8;

  return (
    <IllustrationCard title={t.title} description={t.description(fmt(currentDiv))}>
      <svg viewBox={`0 0 ${W} ${totalH}`} className="mx-auto w-full max-w-xl">
        <PriceAxis />
        <DividendAxis />
        <DividendBars />
        <PriceLine />

        <BuyCallout ptIndex={0} buyPrice={YEAR_DATA[EARLY.idx].price} color={EARLY.color} />
        <BuyCallout ptIndex={LATE.idx} buyPrice={YEAR_DATA[LATE.idx].price} color={LATE.color} textAnchor="end" />

        <text x={W / 2} y={compY - 6} textAnchor="middle" fontSize="8.5" fill="currentColor" opacity={0.55}>
          {t.bothHold(fmt(currentDiv))}
        </text>

        <InvestorCard
          x={PAD}
          y={compY}
          width={colW}
          height={colH}
          label={t.early.label}
          buyYear={t.early.buyYear}
          buyPrice={YEAR_DATA[EARLY.idx].price}
          currentDiv={currentDiv}
          color={EARLY.color}
        />
        <InvestorCard
          x={PAD + colW + colGap}
          y={compY}
          width={colW}
          height={colH}
          label={t.late.label}
          buyYear={t.late.buyYear}
          buyPrice={YEAR_DATA[LATE.idx].price}
          currentDiv={currentDiv}
          color={LATE.color}
        />
      </svg>
    </IllustrationCard>
  );
}
